import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket, BookOpen, Plus, ArrowRight, Clock } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';
import StatusBadge from '../components/tickets/StatusBadge';
import PriorityBadge from '../components/tickets/PriorityBadge';
import { parseApiDate } from '../lib/utils';

export default function DashboardPage() {
  const { user, isStaff } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats]               = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loadingStats, setLoadingStats]   = useState(true);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadingKB, setLoadingKB]         = useState(true);

  useEffect(() => {
    // Fetch each section independently so one failure doesn't blank the page
    api.get('/tickets/stats')
      .then(d => setStats(d.stats || {}))
      .catch(err => { console.error('Stats error:', err); setStats({}); })
      .finally(() => setLoadingStats(false));

    api.get('/tickets?limit=8')
      .then(d => setRecentTickets(d.tickets || []))
      .catch(err => { console.error('Tickets error:', err); setRecentTickets([]); })
      .finally(() => setLoadingTickets(false));

    api.get('/kb/articles?status=published&limit=4')
      .then(d => setRecentArticles((d.articles || []).slice(0, 4)))
      .catch(err => { console.error('KB error:', err); setRecentArticles([]); })
      .finally(() => setLoadingKB(false));
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const statCards = [
    { label: 'Total Tickets',  value: stats?.total      ?? '—', color: null },
    { label: 'Open',           value: stats?.open_count ?? '—', color: '#2563eb' },
    { label: 'In Progress',    value: stats?.in_progress ?? '—', color: '#d97706' },
    { label: 'Resolved',       value: stats?.resolved   ?? '—', color: '#16a34a' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="page-header">
        <h2>Dashboard</h2>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/tickets/new')}>
          <Plus size={14} /> New Ticket
        </button>
      </div>

      <div className="page-body">
        {/* Greeting */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, marginBottom: 4 }}>
            {greeting()}, {user?.full_name?.split(' ')[0] || 'there'} 👋
          </h1>
          <p className="text-secondary text-sm">
            Here's what's happening with your support tickets.
          </p>
        </div>

        {/* Stats */}
        <div className="stats-grid">
          {statCards.map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-label" style={s.color ? { color: s.color } : {}}>{s.label}</div>
              {loadingStats ? (
                <div style={{ marginTop: 8 }}><div className="spinner" style={{ width: 16, height: 16 }} /></div>
              ) : (
                <div className="stat-value" style={s.color ? { color: s.color } : {}}>{s.value}</div>
              )}
            </div>
          ))}
        </div>

        {/* Main grid */}
        <div className="dashboard-grid">

          {/* Recent Tickets */}
          <div className="card" style={{ padding: 0 }}>
            <div className="flex justify-between items-center" style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: 14 }}>Recent Tickets</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/tickets')}>
                View all <ArrowRight size={13} />
              </button>
            </div>

            {loadingTickets ? (
              <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}>
                <div className="spinner" />
              </div>
            ) : recentTickets.length === 0 ? (
              <div className="empty-state">
                <Ticket size={32} />
                <h3>No tickets yet</h3>
                <p>Create your first support ticket</p>
                <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => navigate('/tickets/new')}>
                  <Plus size={13} /> New Ticket
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="dashboard-tickets-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Subject</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTickets.map(t => (
                      <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/tickets/${t.id}`)}>
                        <td><span className="ticket-num">#{t.ticket_number}</span></td>
                        <td>
                          <div className="truncate font-medium" style={{ maxWidth: 260 }}>{t.subject}</div>
                          {t.org && <div className="text-muted text-sm truncate">{t.org.name}</div>}
                        </td>
                        <td>{t.status && <StatusBadge status={t.status} />}</td>
                        <td>{t.priority && <PriorityBadge priority={t.priority} />}</td>
                        <td className="text-muted text-sm" style={{ whiteSpace: 'nowrap' }}>
                          {formatDistanceToNow(parseApiDate(t.updated_at), { addSuffix: true })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Popular KB Articles */}
            <div className="card" style={{ padding: 0 }}>
              <div className="flex justify-between items-center" style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: 14 }}>Popular Articles</h3>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/knowledgebase')}>
                  All <ArrowRight size={13} />
                </button>
              </div>

              {loadingKB ? (
                <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
                  <div className="spinner" />
                </div>
              ) : recentArticles.length === 0 ? (
                <div className="empty-state" style={{ padding: 28 }}>
                  <BookOpen size={24} />
                  <p style={{ fontSize: 12 }}>No published articles yet</p>
                </div>
              ) : (
                recentArticles.map((a, i) => (
                  <div key={a.id} style={{
                    padding: '11px 20px',
                    borderBottom: i < recentArticles.length - 1 ? '1px solid var(--border)' : 'none',
                    cursor: 'pointer',
                  }} onClick={() => navigate(`/knowledgebase/${a.slug || a.id}`)}>
                    <div className="font-medium truncate" style={{ fontSize: 13, marginBottom: 3 }}>{a.title}</div>
                    <div className="flex gap-2 items-center">
                      {a.category?.name && <span className="text-muted text-sm">{a.category.name}</span>}
                      <span className="text-muted text-sm">{a.views ?? 0} views</span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Quick actions */}
            <div className="card card-sm">
              <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 12, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Quick Actions
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button className="btn btn-secondary w-full" onClick={() => navigate('/tickets/new')} style={{ justifyContent: 'flex-start' }}>
                  <Plus size={14} /> New Ticket
                </button>
                <button className="btn btn-secondary w-full" onClick={() => navigate('/tickets')} style={{ justifyContent: 'flex-start' }}>
                  <Ticket size={14} /> All Tickets
                </button>
                <button className="btn btn-secondary w-full" onClick={() => navigate('/knowledgebase')} style={{ justifyContent: 'flex-start' }}>
                  <BookOpen size={14} /> Knowledgebase
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
